/**
 * Conference Calling Utilities for VoiceSphereAI
 *
 * Provides multi-party conference calling functionality (up to 5 participants + host)
 * using WebAudio API for client-side audio mixing. Supports merging multiple active
 * SIP sessions into a single conference call.
 *
 * CONFERENCE FLOW:
 * ---------------
 * 1. Host calls multiple participants (A, B, C, D...)
 * 2. Each new call automatically puts previous calls on hold
 * 3. Once all calls are established, host clicks "Merge Calls"
 * 4. System creates mixed audio for each participant:
 *    - A hears: Host + B + C + D
 *    - B hears: Host + A + C + D
 *    - C hears: Host + A + B + D
 *    - Host hears: A + B + C + D
 *
 * Features:
 * - Audio mixing using Web Audio API (supports 2-5 participants + host)
 * - Dynamic session state management for N-party conferences
 * - Hold/unhold coordination across conference participants
 * - Independent mute controls for host and each participant
 * - Audio device synchronization
 */

import { store, getSessionById } from 'src/store';
import { createConference as createConferenceAction } from 'src/store/slices/call-slice';

import { CALL_STATUS } from './sip';

/**
 * Mix multiple audio streams into a single stream using Web Audio API
 * @param {MediaStream[]} audioStreams - Array of MediaStreams with audio tracks
 * @returns {MediaStream} - Mixed audio stream
 */
export function mixAudioStreams(audioStreams) {
  if (!audioStreams || audioStreams.length === 0) {
    console.warn('No audio streams provided for mixing');
    return null;
  }

  // Create audio context for mixing
  let audioContext = null;
  try {
    // Support for legacy browsers (Safari, older Chrome)
    // Using bracket notation to avoid TypeScript errors
    // eslint-disable-next-line dot-notation
    const AudioContextClass = window.AudioContext || window['webkitAudioContext'];
    audioContext = new AudioContextClass();
  } catch (e) {
    console.error('AudioContext() not available, cannot mix audio:', e);
    return audioStreams[0]; // Return first stream as fallback
  }

  console.log('[Conference Mix] Creating audio mix with', audioStreams.length, 'streams');

  // Create destination for mixed audio
  const mixedAudioDestination = audioContext.createMediaStreamDestination();

  // Connect each audio stream to the mixer
  audioStreams.forEach((stream, index) => {
    if (stream && stream.getAudioTracks().length > 0) {
      console.log(`[Conference Mix] Stream ${index}:`, stream.getAudioTracks().length, 'tracks');
      stream.getAudioTracks().forEach((audioTrack) => {
        console.log(
          `[Conference Mix]   - Track:`,
          audioTrack.label,
          'enabled:',
          audioTrack.enabled
        );
        const srcStream = new MediaStream();
        srcStream.addTrack(audioTrack);
        const streamSourceNode = audioContext.createMediaStreamSource(srcStream);
        streamSourceNode.connect(mixedAudioDestination);
      });
    } else {
      console.warn(`[Conference Mix] Stream ${index} has no audio tracks`);
    }
  });

  const resultStream = mixedAudioDestination.stream;
  console.log(
    '[Conference Mix] Mixed stream created with',
    resultStream.getAudioTracks().length,
    'tracks'
  );

  return resultStream;
}

/**
 * Get remote audio stream from a SIP session
 * @param {Object} session - Normalized session object
 * @returns {MediaStream|null} - Remote audio stream or null
 */
export function getRemoteAudioStream(session) {
  if (!session || !session.rawSession) {
    return null;
  }

  try {
    const pc = session.rawSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) {
      return null;
    }

    const remoteStream = new MediaStream();
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track && receiver.track.kind === 'audio') {
        remoteStream.addTrack(receiver.track);
      }
    });

    return remoteStream.getAudioTracks().length > 0 ? remoteStream : null;
  } catch (error) {
    console.error('Error getting remote audio stream:', error);
    return null;
  }
}

/**
 * Get local audio track from a SIP session
 * @param {Object} session - Normalized session object
 * @returns {MediaStreamTrack|null} - Local audio track or null
 */
export function getLocalAudioTrack(session) {
  if (!session || !session.rawSession) {
    return null;
  }

  try {
    const pc = session.rawSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) {
      return null;
    }

    const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
    return sender ? sender.track : null;
  } catch (error) {
    console.error('Error getting local audio track:', error);
    return null;
  }
}

/**
 * Replace audio track for a SIP session
 * @param {Object} session - Normalized session object
 * @param {MediaStreamTrack} newTrack - New audio track to use
 * @returns {Promise<boolean>} - Success status
 */
export async function replaceSessionAudioTrack(session, newTrack) {
  if (!session || !session.rawSession || !newTrack) {
    console.error('[Replace Track] Invalid parameters');
    return false;
  }

  try {
    const pc = session.rawSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) {
      console.error(`[Replace Track] No peer connection for session ${session.id}`);
      return false;
    }

    // Audio Quality Check: Verify new track before replacement
    console.log(`[Replace Track] 🎵 Pre-replacement quality check for session ${session.id}:`);
    console.log(`[Replace Track]   New track enabled: ${newTrack.enabled}`);
    console.log(`[Replace Track]   New track readyState: ${newTrack.readyState}`);
    console.log(`[Replace Track]   New track muted: ${newTrack.muted}`);
    console.log(`[Replace Track]   New track label: ${newTrack.label}`);

    if (!newTrack.enabled) {
      console.error(`[Replace Track] ✗ New track is DISABLED before replacement!`);
      return false;
    }

    if (newTrack.readyState !== 'live') {
      console.error(
        `[Replace Track] ✗ New track is NOT LIVE before replacement (${newTrack.readyState})!`
      );
      return false;
    }

    const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
    if (sender) {
      const oldTrack = sender.track;
      console.log(`[Replace Track]   Old track label: ${oldTrack?.label || 'none'}`);

      await sender.replaceTrack(newTrack);
      console.log(`[Replace Track] ✓ Track replaced successfully`);

      // CRITICAL FIX: Wait for SDP renegotiation to complete
      // This ensures the remote party receives the new track
      console.log(`[Replace Track] ⏳ Waiting for SDP renegotiation...`);
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Audio Quality Check: Verify after replacement
      const verifyTrack = sender.track;
      console.log(`[Replace Track] 🎵 Post-replacement verification:`);
      console.log(`[Replace Track]   Current track enabled: ${verifyTrack?.enabled}`);
      console.log(`[Replace Track]   Current track readyState: ${verifyTrack?.readyState}`);
      console.log(`[Replace Track]   Current track muted: ${verifyTrack?.muted}`);

      if (verifyTrack?.enabled && verifyTrack?.readyState === 'live') {
        console.log(`[Replace Track] ✓✓ Audio quality verified - track is live and enabled`);

        // CRITICAL FIX: Ensure audio element is playing
        const audioElement = document.getElementById(`call-audio-${session.id}`);
        if (audioElement) {
          if (audioElement.paused) {
            console.log(`[Replace Track] 🔊 Resuming audio playback for session ${session.id}`);
            try {
              await audioElement.play();
              console.log(`[Replace Track] ✓ Audio playback resumed`);
            } catch (playError) {
              console.warn(
                `[Replace Track] Audio play error (may be user interaction required):`,
                playError
              );
            }
          }
        }

        return true;
      }
      console.error(`[Replace Track] ✗ Audio quality check failed after replacement!`);
      return false;
    }

    console.error(`[Replace Track] ✗ No audio sender found for session ${session.id}`);
    return false;
  } catch (error) {
    console.error(
      `[Replace Track] ✗ Error replacing audio track for session ${session.id}:`,
      error
    );
    return false;
  }
}

/**
 * Create mixed audio for multi-party conference calling
 *
 * AUDIO MIXING STRATEGY:
 * For each participant session, create a mixed stream containing:
 * 1. Host's microphone (this session's sender track)
 * 2. Audio from ALL OTHER participants (receivers from all other sessions)
 *
 * Example with 4 participants (A, B, C):
 * - Mixed track for A contains: Host mic + B's audio + C's audio
 * - Mixed track for B contains: Host mic + A's audio + C's audio
 * - Mixed track for C contains: Host mic + A's audio + B's audio
 *
 * @param {Array<Object>} sessions - Array of session objects to merge into conference
 * @returns {Object} - { success: boolean, mixedTracks: [], originalTracks: {}, error?: string }
 */
export async function createConferenceAudioMix(...sessions) {
  // Support both array and individual arguments
  const sessionArray = Array.isArray(sessions[0]) ? sessions[0] : sessions;

  console.log(`[Conference Mix] Starting with ${sessionArray.length} sessions`);

  if (sessionArray.length < 2) {
    return { success: false, error: 'At least 2 sessions required for conference' };
  }

  try {
    const mixedTracks = [];
    const originalTracks = {};

    // Validate all sessions have proper peer connections
    for (let i = 0; i < sessionArray.length; i += 1) {
      const session = sessionArray[i];
      const pc = session.rawSession?.sessionDescriptionHandler?.peerConnection;

      if (!pc) {
        console.error(`[Conference Mix] Session ${i} (${session.id}) has no peer connection`);
        throw new Error(`Session ${session.id} has no peer connection`);
      }
      console.log(`[Conference Mix] Session ${i} (${session.id}) validated - has peer connection`);
    }

    // CRITICAL FIX: Ensure all receiver tracks are enabled before mixing
    console.log('[Conference Mix] 🔍 Verifying all receiver tracks are enabled...');
    for (let i = 0; i < sessionArray.length; i += 1) {
      const session = sessionArray[i];
      const pc = session.rawSession.sessionDescriptionHandler.peerConnection;

      pc.getReceivers().forEach((receiver) => {
        if (receiver.track && receiver.track.kind === 'audio') {
          if (!receiver.track.enabled) {
            console.warn(
              `[Conference Mix] ⚠️  Enabling disabled receiver track for session ${session.id}`
            );
            receiver.track.enabled = true;
          }
        }
      });
    }
    console.log('[Conference Mix] ✓ All receiver tracks verified');

    // For each session, create a mixed audio stream
    for (let i = 0; i < sessionArray.length; i += 1) {
      const currentSession = sessionArray[i];
      const pc = currentSession.rawSession.sessionDescriptionHandler.peerConnection;

      console.log(
        `[Conference Mix] Creating mix ${i + 1}/${sessionArray.length} for session ${currentSession.id}`
      );

      const outputStream = new MediaStream();

      // 1. Add host's microphone from THIS session's sender
      // CRITICAL FIX: Use stored original track if session was already in a conference
      // Otherwise, use current sender track
      let originalTrack = null;
      let trackToUse = null;

      // Check if this session already has an original track stored (was in previous conference)
      if (currentSession.rawSession.originalAudioTrack) {
        originalTrack = currentSession.rawSession.originalAudioTrack;
        trackToUse = originalTrack;
        console.log(
          `[Conference Mix]   ✓ Using STORED original track for session ${i} (was in previous conference)`
        );
        console.log(
          `[Conference Mix]     Track: enabled=${trackToUse.enabled}, readyState=${trackToUse.readyState}`
        );
      } else {
        // This is a new session, use current sender track
        const senders = pc.getSenders();
        console.log(`[Conference Mix]   Session ${i} has ${senders.length} senders`);

        senders.forEach((sender, idx) => {
          if (sender.track && sender.track.kind === 'audio') {
            console.log(
              `[Conference Mix]   ✓ Using current sender track (sender ${idx}) for new session ${i}`
            );
            originalTrack = sender.track;
            trackToUse = sender.track;
          }
        });
      }

      if (trackToUse) {
        // CRITICAL FIX: Ensure track is enabled
        if (!trackToUse.enabled) {
          console.warn(`[Conference Mix]   ⚠️  Enabling disabled track`);
          trackToUse.enabled = true;
        }
        outputStream.addTrack(trackToUse);
        originalTracks[currentSession.id] = originalTrack;
        console.log(
          `[Conference Mix]   ✓ Added and stored original track for session ${currentSession.id}`
        );
      } else {
        console.warn(`[Conference Mix]   ⚠ No track found for session ${currentSession.id}`);
      }

      // 2. Add audio from ALL OTHER sessions (their receivers)
      for (let j = 0; j < sessionArray.length; j += 1) {
        if (i === j) {
          // Skip self - cannot add own audio to own mix
        } else {
          const otherSession = sessionArray[j];
          const otherPc = otherSession.rawSession.sessionDescriptionHandler.peerConnection;
          const receivers = otherPc.getReceivers();

          console.log(
            `[Conference Mix]   Session ${j} (${otherSession.id}) has ${receivers.length} receivers`
          );

          let audioReceiversAdded = 0;
          receivers.forEach((receiver, idx) => {
            if (receiver.track && receiver.track.kind === 'audio') {
              console.log(
                `[Conference Mix]   ✓ Adding session ${j} audio (receiver ${idx}) to mix ${i}`
              );
              console.log(
                `[Conference Mix]     Receiver track: enabled=${receiver.track.enabled}, readyState=${receiver.track.readyState}, label=${receiver.track.label}`
              );
              // CRITICAL FIX: Ensure receiver track is enabled
              if (!receiver.track.enabled) {
                console.warn(
                  `[Conference Mix]   ⚠️  Enabling disabled receiver track from session ${j}`
                );
                receiver.track.enabled = true;
              }
              outputStream.addTrack(receiver.track);
              audioReceiversAdded += 1;
            }
          });

          console.log(
            `[Conference Mix]   ✓ Added ${audioReceiversAdded} audio receiver(s) from session ${j} to mix ${i}`
          );
        }
      }

      console.log(
        `[Conference Mix]   Output stream for session ${i} has ${outputStream.getAudioTracks().length} tracks`
      );

      // Audio Quality Check: Verify all input tracks are enabled and live
      const inputTracks = outputStream.getAudioTracks();
      console.log(`[Conference Mix]   🎵 Audio Quality Check for session ${i}:`);
      let hasDisabledTracks = false;
      let hasNonLiveTracks = false;

      inputTracks.forEach((track, trackIdx) => {
        console.log(
          `[Conference Mix]     Track ${trackIdx}: enabled=${track.enabled}, readyState=${track.readyState}, label=${track.label}`
        );
        if (!track.enabled) {
          console.error(
            `[Conference Mix]     ✗ Track ${trackIdx} is DISABLED - this will cause audio issues!`
          );
          hasDisabledTracks = true;
        }
        if (track.readyState !== 'live') {
          console.error(
            `[Conference Mix]     ✗ Track ${trackIdx} is NOT LIVE (${track.readyState}) - audio will not work!`
          );
          hasNonLiveTracks = true;
        }
      });

      if (hasDisabledTracks || hasNonLiveTracks) {
        throw new Error(
          `Invalid track state detected for session ${currentSession.id} - cannot create reliable audio mix`
        );
      }

      // Mix the combined stream
      const mixedStream = mixAudioStreams([outputStream]);
      const mixedTrack = mixedStream?.getAudioTracks()[0];

      if (!mixedTrack) {
        console.error(
          `[Conference Mix]   ✗ Failed to create mixed track for session ${currentSession.id}`
        );
        throw new Error(`Failed to create mixed track for session ${currentSession.id}`);
      }

      // Audio Quality Check: Verify mixed track
      console.log(`[Conference Mix]   🎵 Mixed track quality check:`);
      console.log(`[Conference Mix]     enabled: ${mixedTrack.enabled}`);
      console.log(`[Conference Mix]     readyState: ${mixedTrack.readyState}`);
      console.log(`[Conference Mix]     muted: ${mixedTrack.muted}`);
      console.log(`[Conference Mix]     label: ${mixedTrack.label}`);

      if (!mixedTrack.enabled) {
        console.error(`[Conference Mix]   ✗ Mixed track is DISABLED - audio will not work!`);
        throw new Error(`Mixed track for session ${currentSession.id} is disabled`);
      }

      if (mixedTrack.readyState !== 'live') {
        console.error(
          `[Conference Mix]   ✗ Mixed track is NOT LIVE (${mixedTrack.readyState}) - audio will not work!`
        );
        throw new Error(`Mixed track for session ${currentSession.id} is not live`);
      }

      mixedTrack.isMixedTrack = true;
      mixedTracks.push({
        sessionId: currentSession.id,
        mixedTrack,
      });

      console.log(
        `[Conference Mix]   ✓✓ Mixed track VERIFIED for session ${i} - audio should be clear`
      );
    }

    console.log(`[Conference Mix] ✓✓✓ Successfully created ${mixedTracks.length} mixed tracks`);
    console.log(`[Conference Mix] Session IDs: ${mixedTracks.map((m) => m.sessionId).join(', ')}`);

    return {
      success: true,
      mixedTracks, // [{ sessionId, mixedTrack }, ...]
      originalTracks, // { sessionId: originalTrack, ... }
    };
  } catch (error) {
    console.error('[Conference Mix] ✗✗✗ Error creating conference audio mix:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if a session is currently in a conference
 * @param {Object} session - Normalized session object
 * @returns {boolean}
 */
export function isSessionInConference(session) {
  return session && session.isConference === true && session.conferenceGroupId;
}

/**
 * Get all sessions in a conference group
 * @param {string} conferenceGroupId - Conference group identifier
 * @returns {Array} - Array of sessions in the conference
 */
export function getConferenceSessions(conferenceGroupId) {
  const { sessions } = store.getState().call;
  return sessions.filter((s) => s.conferenceGroupId === conferenceGroupId);
}

/**
 * Get the active conference group ID if one exists
 * @returns {string|null} - Conference group ID or null
 */
export function getActiveConferenceId() {
  const { conferences } = store.getState().call;
  const conferenceIds = Object.keys(conferences);
  return conferenceIds.length > 0 ? conferenceIds[0] : null;
}

/**
 * Add new participants to an existing conference
 * Recreates the audio mix with all participants (existing + new)
 * @param {string} conferenceGroupId - Existing conference group ID
 * @param {Array<Object>} newSessions - New sessions to add to conference
 * @returns {Promise<Object>} - { success: boolean, error?: string }
 */
export async function addParticipantsToConference(conferenceGroupId, newSessions) {
  try {
    console.log(
      `[Conference] Adding ${newSessions.length} participants to conference ${conferenceGroupId}`
    );

    // Get existing conference sessions
    const existingSessions = getConferenceSessions(conferenceGroupId);

    if (existingSessions.length === 0) {
      return { success: false, error: 'Conference not found or has no sessions' };
    }

    console.log(`[Conference] Existing conference has ${existingSessions.length} participants`);

    // Validate new sessions can be added
    for (let i = 0; i < newSessions.length; i += 1) {
      const session = newSessions[i];
      if (!session || !session.rawSession) {
        return { success: false, error: 'Invalid session provided' };
      }
      if (isSessionInConference(session)) {
        return { success: false, error: `Session ${session.id} is already in a conference` };
      }
    }

    // Combine all sessions (existing + new)
    const allSessions = [...existingSessions, ...newSessions];
    console.log(`[Conference] Total participants after adding: ${allSessions.length}`);

    // Recreate audio mix for all participants
    const audioMix = await createConferenceAudioMix(allSessions);

    if (!audioMix.success) {
      return { success: false, error: audioMix.error || 'Failed to create audio mix' };
    }

    console.log(`[Conference] Created new audio mix with ${audioMix.mixedTracks.length} tracks`);

    // Replace audio tracks for ALL sessions (both existing and new)
    for (let i = 0; i < audioMix.mixedTracks.length; i += 1) {
      const mixData = audioMix.mixedTracks[i];
      const session = allSessions.find((s) => s.id === mixData.sessionId);
      if (session) {
        // eslint-disable-next-line no-await-in-loop
        const result = await replaceSessionAudioTrack(session, mixData.mixedTrack);
        if (!result) {
          console.warn(`[Conference] Failed to replace track for session ${mixData.sessionId}`);
        }
        // Store original track for restoration
        session.rawSession.originalAudioTrack = audioMix.originalTracks[mixData.sessionId];
        session.rawSession.conferenceGroupId = conferenceGroupId;
      }
    }

    // Update Redux: Add new session IDs to conference
    const allSessionIds = allSessions.map((s) => s.id);

    // Prepare audio data for Redux
    const audioDataForRedux = {};
    audioMix.mixedTracks.forEach((mixData) => {
      audioDataForRedux[`mixed_${mixData.sessionId}`] = {
        id: mixData.mixedTrack.id,
        label: mixData.mixedTrack.label,
      };
    });

    Object.keys(audioMix.originalTracks).forEach((sessionId) => {
      audioDataForRedux[`original_${sessionId}`] = {
        id: audioMix.originalTracks[sessionId].id,
        label: audioMix.originalTracks[sessionId].label,
      };
    });

    // Update conference in Redux with new session IDs
    store.dispatch(
      createConferenceAction({
        groupId: conferenceGroupId,
        sessionIds: allSessionIds,
        audioData: audioDataForRedux,
      })
    );

    console.log(`[Conference] Successfully added ${newSessions.length} participants to conference`);

    return { success: true, participantCount: allSessions.length };
  } catch (error) {
    console.error('[Conference] Error adding participants to conference:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Generate unique conference group ID
 * @returns {string} - Unique conference identifier
 */
export function generateConferenceId() {
  return `conf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Validate that sessions can be merged into a conference
 * Supports both 2-session validation and multi-session validation
 * @param {...Object} sessions - Session objects to validate (can be 2 or more)
 * @returns {Object} - { valid: boolean, reason?: string }
 */
export function canMergeSessions(...sessions) {
  const sessionArray = Array.isArray(sessions[0]) ? sessions[0] : sessions;

  if (sessionArray.length < 2) {
    return { valid: false, reason: 'At least 2 sessions required for merge' };
  }

  // Check for duplicate session IDs
  const sessionIds = new Set();
  for (let i = 0; i < sessionArray.length; i += 1) {
    const session = sessionArray[i];
    if (!session) {
      return { valid: false, reason: 'Invalid session object provided' };
    }
    if (sessionIds.has(session.id)) {
      return { valid: false, reason: 'Cannot merge a session with itself' };
    }
    sessionIds.add(session.id);
  }

  // Check each session is established
  for (let i = 0; i < sessionArray.length; i += 1) {
    const session = sessionArray[i];
    if (session.state !== CALL_STATUS.ESTABLISHED) {
      return { valid: false, reason: 'All sessions must be established (answered)' };
    }
  }

  // Check no session is already in a conference
  for (let i = 0; i < sessionArray.length; i += 1) {
    const session = sessionArray[i];
    if (isSessionInConference(session)) {
      return { valid: false, reason: 'One or more sessions are already in a conference' };
    }
  }

  // Check no session is being transferred
  for (let i = 0; i < sessionArray.length; i += 1) {
    const session = sessionArray[i];
    if (session.isBeingTransferred) {
      return { valid: false, reason: 'Cannot merge sessions during transfer' };
    }
  }

  return { valid: true };
}

/**
 * Stop and clean up mixed audio tracks
 * @param {Object} conferenceData - Conference data with tracks to clean up
 */
export function cleanupConferenceAudio(conferenceData) {
  if (!conferenceData) return;

  try {
    if (conferenceData.mixedTrackForA && conferenceData.mixedTrackForA.stop) {
      conferenceData.mixedTrackForA.stop();
    }
    if (conferenceData.mixedTrackForB && conferenceData.mixedTrackForB.stop) {
      conferenceData.mixedTrackForB.stop();
    }
  } catch (error) {
    console.error('Error cleaning up conference audio:', error);
  }
}

/**
 * Restore original audio tracks after conference ends
 * @param {Object} session - Session to restore
 * @param {MediaStreamTrack} originalTrack - Original audio track
 * @returns {Promise<boolean>}
 */
export async function restoreOriginalAudio(session, originalTrack) {
  if (!session || !originalTrack) {
    return false;
  }

  try {
    const success = await replaceSessionAudioTrack(session, originalTrack);
    if (success) {
      // Restore mute state
      originalTrack.enabled = !session.isMuted;
    }
    return success;
  } catch (error) {
    console.error('Error restoring original audio:', error);
    return false;
  }
}

/**
 * Get the conference partner session
 * @param {string} sessionId - Current session ID
 * @returns {Object|null} - Partner session or null
 */
export function getConferencePartner(sessionId) {
  const session = getSessionById(sessionId);
  if (!isSessionInConference(session)) {
    return null;
  }

  const conferenceSessions = getConferenceSessions(session.conferenceGroupId);
  return conferenceSessions.find((s) => s.id !== sessionId) || null;
}

/**
 * Log conference event for debugging
 * @param {string} event - Event name
 * @param {Object} data - Event data
 */
export function logConferenceEvent(event, data) {
  console.log(`[Conference] ${event}:`, data);
}

/**
 * Mute a specific participant in conference (not heard by anyone)
 * Disables the participant's incoming audio (receivers) so they can't be heard
 * @param {string} sessionId - Session ID of the participant to mute
 * @returns {Promise<boolean>}
 */
export async function muteConferenceParticipant(sessionId) {
  const session = getSessionById(sessionId);
  if (!session || !session.rawSession) {
    return false;
  }

  try {
    const pc = session.rawSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) return false;

    // Disable the RECEIVER tracks (incoming audio from this participant)
    // This prevents their voice from being heard in the conference
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track && receiver.track.kind === 'audio') {
        receiver.track.enabled = false;
        console.log(`[Conference] Muted participant ${sessionId} (receiver track disabled)`);
      }
    });

    // Also mute the audio element so host doesn't hear this participant
    const audioElement = document.getElementById(`call-audio-${sessionId}`);
    if (audioElement) {
      audioElement.muted = true;
    }

    return true;
  } catch (error) {
    console.error('Error muting conference participant:', error);
    return false;
  }
}

/**
 * Unmute a specific participant in conference
 * Enables the participant's incoming audio (receivers) so they can be heard
 * @param {string} sessionId - Session ID of the participant to unmute
 * @returns {Promise<boolean>}
 */
export async function unmuteConferenceParticipant(sessionId) {
  const session = getSessionById(sessionId);
  if (!session || !session.rawSession) {
    return false;
  }

  try {
    const pc = session.rawSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) return false;

    // Enable the RECEIVER tracks (incoming audio from this participant)
    // This allows their voice to be heard in the conference again
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track && receiver.track.kind === 'audio') {
        receiver.track.enabled = true;
        console.log(`[Conference] Unmuted participant ${sessionId} (receiver track enabled)`);
      }
    });

    // Unmute the audio element so host can hear this participant
    const audioElement = document.getElementById(`call-audio-${sessionId}`);
    if (audioElement) {
      audioElement.muted = false;
    }

    return true;
  } catch (error) {
    console.error('Error unmuting conference participant:', error);
    return false;
  }
}

/**
 * Mute host in conference (all participants can still hear each other)
 * This only mutes the HOST's microphone in the mixed tracks
 * @param {string} conferenceGroupId - Conference group identifier
 * @returns {Promise<boolean>}
 */
export async function muteHostInConference(conferenceGroupId) {
  const conferenceSessions = getConferenceSessions(conferenceGroupId);
  if (conferenceSessions.length < 2) {
    console.warn('Conference must have at least 2 sessions');
    return false;
  }

  try {
    // Mute host's microphone in ALL conference sessions
    let mutedCount = 0;
    for (let i = 0; i < conferenceSessions.length; i += 1) {
      const session = conferenceSessions[i];
      const originalTrack = session.rawSession?.originalAudioTrack;

      if (originalTrack) {
        originalTrack.enabled = false;
        mutedCount += 1;
      }
    }

    if (mutedCount === 0) {
      console.warn('[Conference] No original audio tracks found on sessions');
      return false;
    }

    console.log(
      `[Conference] Muted host microphone in ${mutedCount} sessions (participants can still hear each other)`
    );

    return true;
  } catch (error) {
    console.error('Error muting host in conference:', error);
    return false;
  }
}

/**
 * Unmute host in conference
 * @param {string} conferenceGroupId - Conference group identifier
 * @returns {Promise<boolean>}
 */
export async function unmuteHostInConference(conferenceGroupId) {
  const conferenceSessions = getConferenceSessions(conferenceGroupId);
  if (conferenceSessions.length < 2) {
    console.warn('Conference must have at least 2 sessions');
    return false;
  }

  try {
    // Unmute host's microphone in ALL conference sessions
    let unmutedCount = 0;
    for (let i = 0; i < conferenceSessions.length; i += 1) {
      const session = conferenceSessions[i];
      const originalTrack = session.rawSession?.originalAudioTrack;

      if (originalTrack) {
        originalTrack.enabled = true;
        unmutedCount += 1;
      }
    }

    if (unmutedCount === 0) {
      console.warn('[Conference] No original audio tracks found on sessions');
      return false;
    }

    console.log(`[Conference] Unmuted host microphone in ${unmutedCount} sessions`);

    return true;
  } catch (error) {
    console.error('Error unmuting host in conference:', error);
    return false;
  }
}
